import React, { Component } from 'react';

import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import { FormattedMessage, injectIntl, type IntlShape } from '@atlassian/jira-intl';
import {
	ButtonGroup,
	Lozenge,
	IconButton,
	Tooltip,
	SpotlightTarget,
} from '@atlassian/jira-portfolio-3-common';
import { FeedbackButtonLazyComponent } from '@atlassian/jira-portfolio-3-feedback-collector';
import {
	EDIT,
	OPTIMIZED,
	OPTIMIZING,
	REPORT,
	READONLY,
	EXPORT,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/app';
import ViewSettings from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/view-settings';
import FilterBar from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/filter-bar';
import ViewBar from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/view-bar';
import ViewSwitcher from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/view-bar/view-switcher';
import {
	TIMELINE_SHARE_TYPE,
	DEPENDENCY_REPORT_SHARE_TYPE,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { Row, Left, Right } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/layout/row';
import { PLAN_PAGES } from '@atlassian/jira-portfolio-3-portfolio/src/common/window';

import ApplyOptimisations from './apply-optimisation';
import messages from './messages';
import Optimize from './optimize';
import PlanSettings from './plan-settings';
import Scenarios from './scenarios';
import ShareReport from './share-report';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import Title from './title';
import type { Props } from './types';
import UpdateJira from './update-jira';
import WarningCenter from './warnings';

// eslint-disable-next-line jira/react/no-class-components
class TitleBar extends Component<
	Props & {
		intl: IntlShape;
	}
> {
	// eslint-disable-next-line react/sort-comp
	static defaultProps = {
		Optimize,
		ApplyOptimisations,
		PlanSettings,
		Title,
		UpdateJira,
		ShareReport,
		WarningCenter,
		Scenarios,
		FeedbackButton: FeedbackButtonLazyComponent,
		FilterBar,
		ViewBar,
		ViewSettings,
		ViewSwitcher,
	};

	static displayName = 'TitleBar';

	static whyDidYouRender = true;

	getReadOnlyPlanLozenge = () => (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className={styles.readOnlyPlanLozenge}>
			<Lozenge
				isBold
				testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.read-only-lozenge"
			>
				<FormattedMessage {...messages.readOnlyLozengeText} />
			</Lozenge>
		</div>
	);

	getToggleToolBarButtonRow = (currentRoute: string) => {
		const { intl, mode, showToolBar, toggleToolBar } = this.props;

		// hiding the collapse / expand icon when the plan is optimized the header is hidden by an overlay,
		// but the z-index of the button would make it appear over the overlay
		if (currentRoute !== PLAN_PAGES.ROADMAP || mode === OPTIMIZED) {
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<Row className={styles.headerRowSeparator}>
					<></>
				</Row>
			);
		}

		const buttonMessage = intl.formatMessage(
			showToolBar ? messages.collapseToolBar : messages.expandToolBar,
		);

		return (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			<Row className={styles.headerRowBacklogSeparator}>
				<div
					data-testid="portfolio-3-portfolio.app-simple-plans.top.title-bar.toggle-toolbar-button"
					id="toolBarButtonIcon"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={styles.toolBarButtonIcon}
				>
					<Tooltip content={buttonMessage} position="right">
						<IconButton onClick={toggleToolBar} ariaLabel={buttonMessage}>
							{showToolBar ? (
								<ChevronUpIcon
									testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.up-icon"
									label=""
								/>
							) : (
								<ChevronDownIcon
									testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.down-icon"
									label=""
								/>
							)}
						</IconButton>
					</Tooltip>
				</div>
			</Row>
		);
	};

	getHeaderRows = () => {
		const {
			// eslint-disable-next-line @typescript-eslint/no-shadow
			ApplyOptimisations,
			FeedbackButton,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			FilterBar,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			Optimize,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			PlanSettings,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			Scenarios,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			ShareReport,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			Title,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			UpdateJira,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			ViewBar,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			ViewSettings,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			ViewSwitcher,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			WarningCenter,
			location: { pathname },
			mode,
			readOnly,
			showToolBar,
			intl: { formatMessage },
		} = this.props;
		const currentRoute = pathname.slice(1).split('/')[1];

		switch (mode) {
			case EDIT:
			case OPTIMIZED:
			case OPTIMIZING:
				return (
					<>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<Row className={styles.headerRow}>
							<section
								aria-label={formatMessage(messages.planSettingsViewTitle)}
								data-testid="portfolio-3-portfolio.app-simple-plans.top.title-bar.mode-edit-optimized-optimizing"
							>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
								<Left className={styles.headerRowLeft}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<div className={styles.title}>
										<Title isReadOnly={readOnly} />
									</div>
									<PlanSettings />
									{readOnly && this.getReadOnlyPlanLozenge()}
									<Scenarios />
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<div className={styles.viewSwitcherWrapper}>
										<ViewSwitcher currentRoute={currentRoute} isReadOnly={readOnly} />
									</div>
								</Left>
							</section>
							<section aria-label={formatMessage(messages.giveFeedbackAndReviewTitle)}>
								<Right>
									<ButtonGroup>
										<FeedbackButton />
										{currentRoute === PLAN_PAGES.DEPENDENCIES_REPORT && showToolBar && (
											<ShareReport confluenceMacroType={DEPENDENCY_REPORT_SHARE_TYPE} />
										)}
										<UpdateJira />
									</ButtonGroup>
								</Right>
							</section>
						</Row>
						{this.getToggleToolBarButtonRow(currentRoute)}
						{currentRoute === PLAN_PAGES.ROADMAP && showToolBar && (
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							<Row className={styles.toolBar}>
								<section aria-label={formatMessage(messages.filterAndChangeViewTitle)}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<Left className={styles.left}>
										<SpotlightTarget name="filters-view-settings">
											{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
											<div className={styles.toolBarFiltersSettingsContainer}>
												<FilterBar />
												<ViewSettings />
											</div>
										</SpotlightTarget>
										<ViewBar />
									</Left>
								</section>
								<section aria-label={formatMessage(messages.warningsAndAutoSceduleTitle)}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<Right className={styles.toolBarRight}>
										<WarningCenter />
										<ShareReport confluenceMacroType={TIMELINE_SHARE_TYPE} />
										<Optimize />
										{mode === OPTIMIZED && <ApplyOptimisations />}
									</Right>
								</section>
							</Row>
						)}
					</>
				);

			case READONLY:
				return (
					<>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<Row className={styles.headerRow}>
							<section
								aria-label={formatMessage(messages.planSettingsViewTitle)}
								data-testid="portfolio-3-portfolio.app-simple-plans.top.title-bar.mode-readonly"
							>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
								<Left className={styles.left}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<div className={styles.title}>
										<Title isReadOnly={readOnly} />
									</div>
									<PlanSettings />
									{readOnly && this.getReadOnlyPlanLozenge()}
									<Scenarios />
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<div className={styles.viewSwitcherWrapper}>
										<ViewSwitcher currentRoute={currentRoute} isReadOnly={readOnly} />
									</div>
								</Left>
							</section>
							<section aria-label={formatMessage(messages.giveFeedbackAndReviewTitle)}>
								<Right>
									<ButtonGroup>
										<FeedbackButton />
										<UpdateJira />
									</ButtonGroup>
								</Right>
							</section>
						</Row>
						{this.getToggleToolBarButtonRow(currentRoute)}
						{currentRoute === PLAN_PAGES.ROADMAP && showToolBar && (
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							<Row className={styles.toolBar}>
								<section aria-label={formatMessage(messages.filterAndChangeViewTitle)}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<Left className={styles.left}>
										<SpotlightTarget name="filters-view-settings">
											{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
											<div className={styles.toolBarFiltersSettingsContainer}>
												<FilterBar />
												<ViewSettings />
											</div>
										</SpotlightTarget>
										<ViewBar />
									</Left>
								</section>
								<section aria-label={formatMessage(messages.warningsAndShareTitle)}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<Right className={styles.toolBarRight}>
										<WarningCenter />
										<ShareReport confluenceMacroType={TIMELINE_SHARE_TYPE} />
									</Right>
								</section>
							</Row>
						)}
					</>
				);
			case REPORT:
				return (
					<>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<Row className={styles.headerRow}>
							<section
								aria-label={formatMessage(messages.planSettingsTitle)}
								data-testid="portfolio-3-portfolio.app-simple-plans.top.title-bar.mode-report"
							>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
								<Left className={styles.left}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<div className={styles.title}>
										<Title isReadOnly={readOnly} />
									</div>
									{readOnly && this.getReadOnlyPlanLozenge()}
									<Scenarios />
								</Left>
							</section>
							<section aria-label={formatMessage(messages.giveFeedbackTitle)}>
								<Right>
									<FeedbackButton />
								</Right>
							</section>
						</Row>
						{this.getToggleToolBarButtonRow(currentRoute)}
						{showToolBar && (
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							<Row className={styles.toolBar}>
								<section aria-label={formatMessage(messages.filterAndChangeViewTitle)}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<Left className={styles.left}>
										{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
										<div className={styles.toolBarFiltersSettingsContainer}>
											<FilterBar />
											<ViewSettings />
										</div>
									</Left>
								</section>
								<section aria-label={formatMessage(messages.warningsAndShareTitle)}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<Right className={styles.toolBarRight}>
										<WarningCenter />
										<ShareReport confluenceMacroType={TIMELINE_SHARE_TYPE} />
									</Right>
								</section>
							</Row>
						)}
					</>
				);
			case EXPORT:
				return null;
			default:
				throw new Error(`Unexpected mode: ${mode}`);
		}
	};

	render() {
		return (
			<div data-testid="portfolio-3-portfolio.app-simple-plans.top.title-bar.title-bar">
				{this.getHeaderRows()}
			</div>
		);
	}
}
export default injectIntl(TitleBar);
